import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import FontAwesome from "../../../utilities/FontAwesome";
import styles from "./ContentItemBlock.module.scss"
import {modalStore, StoreContext} from "../../../../stores/StoreLoader";
import {getPlatform} from "../../../../utils/StringUtilities";
import SocialMarkdown from "../../../utilities/SocialMarkdown";
import moment from "moment/moment";
import {isNews, isEvent, isFile, Media, ItemDetails, isMedia} from "../../../schoolfeed/ListItemGroup";
import classNames from 'classnames';
import ImageComponent from "../../../utilities/ImageComponent";
import {
    getAltText,
    getItemImage,
    IReactiveEventContentItem, IReactiveNewsContentItem, isCoursework,
    SchoolFeedContentItem
} from "../../../../stores/SchoolFeedStore";
import {EditButton} from "../../TerracedGridTheme/EditButton";
import {observer} from "mobx-react";
import {useMountedEffect} from "../../../../hooks/useMountedEffect";
import {isDescendantOrEq} from "../../../../utils/DataUtilities";
import FlatIconSVG from "../../../utilities/FlatIconSVG";

export const handleContentItemClick = (itemObj: ContentItem) => {
    if (/drive.google.com/.test(itemObj.third_party_url)) {
        modalStore.addModal({
            type: "file-preview",
            id: itemObj.id,
            itemObj: { // need to spoof a file item here
                ...itemObj,
                json_data: {
                    file: {
                        view_link: itemObj.third_party_url,
                    },
                },
            },
        })
    } else if (isNews(itemObj) || isMedia(itemObj)) {
        modalStore.addModal({
            type: "news",
            id: itemObj.id,
            itemObj: itemObj,
        })
    } else if (isEvent(itemObj)) {
        modalStore.addModal({
            type: "events",
            itemObj: itemObj,
            id: itemObj.id,
        });
    }
    else if (isFile(itemObj)) {
        modalStore.addModal({
            type: "file-preview",
            itemObj: itemObj,
            id: itemObj.id,
        });
    }
    else if (isCoursework(itemObj)) {
        modalStore.addModal({
            type: "coursework",
            id: itemObj.id,
            itemObj: itemObj,
        });
    }
};

export function ContentItemReadMore(props) {
    const linkClassName = classNames({
        [styles.link]: true,
    })

    return <div>
        <button className={linkClassName} {...props} onClick={props.onClick ? () => props.onClick(props.contentItem) : () => { return }}>
            <span className={styles.button}>Read More</span>
        </button>
    </div>

}

export function getCourseWorkIcons(contentItem) {
    const iconArray: JSX.Element[] = [];

    if (contentItem.json_data.coursework.materials && contentItem.json_data.coursework.materials.length > 0) {
        for (let i=0; i<contentItem.json_data.coursework.materials?.length; i++) {
            if (contentItem.json_data.coursework.materials[i]?.youtubeVideo) {
                iconArray.push(<FontAwesome prefix={"fab"} name={"fa-youtube"} style={{color: 'red'}}></FontAwesome>);
            }
            if (contentItem.json_data.coursework.materials[i]?.driveFile?.driveFile) {
                iconArray.push(<FlatIconSVG name={'drive'}/>);
            }
            if (contentItem.json_data.coursework.materials[i]?.link) {
                iconArray.push(<FontAwesome prefix={"fas"} name={"fa-link"}></FontAwesome>);
            }
            if (contentItem.json_data.coursework.materials[i]?.form) {
                iconArray.push(<FontAwesome prefix={"fas"} name={"fa-rectangle-list"} style={{color: 'purple'}}></FontAwesome>);
            }
        }
        const uniqueIcons = Array.from(new Set(iconArray.map(obj => obj.props.name)))
            .map(name => iconArray.find(obj => obj.props.name === name))
            .sort((a, b) => {
                const nameA = (a && a.props && a.props.name) || '';
                const nameB = (b && b.props && b.props.name) || '';
                return nameA.localeCompare(nameB);
            });

        const courseWorkImageContainerClassName = classNames({
            [styles.courseWorkImageContainer]: true,
            [styles.courseWorkImageContainerGreaterThanTwo]: uniqueIcons.length > 2,
        });

        const image = <ImageComponent alt={"Coursework Image"}
                                      src={require("../../../../../components/assets/images/placeholders/placeholder-coursework.png")}
                                      className={styles.image}
                                      style={{padding: '15%'}}/>
        return <div className={courseWorkImageContainerClassName}>
            {uniqueIcons.length > 0 ?
                uniqueIcons.map((icon, index) => <div key={index}>{icon}</div>)
                : image}
        </div>
    }
    else {
        return null;
    }
}

const ContentItemBlock = observer((props: {
    contentItem: SchoolFeedContentItem
    packeryLayout: () => void
    handleSave?: (c: ContentItem, o: BaseOrganization) => void
    handleDelete?: (c: ContentItem) => void
    unpublishItem: (item: SchoolFeedContentItem) => void,
}) => {
    const {interfaceStore, organizationStore, userStore} = useContext(StoreContext);
    const attribution = getPlatform(props.contentItem.type);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const [blurBottom, setBlurBottom] = useState(false);

    const currentUserCanModifyAtCurrentOrganization = userStore.isEditor &&
        props.contentItem.is_custom &&
        props.contentItem.organization &&
        isDescendantOrEq(props.contentItem.organization.path, organizationStore.currentOrganization.path);

    const itemTitle = isNews(props.contentItem) ? props.contentItem.title : props.contentItem.title;
    const date = moment(props.contentItem.created_at).format('MMM D, YYYY');

    useEffect(() => {
        if (containerRef.current?.clientHeight) {
            if (containerRef.current.clientHeight >= 500) {
                setBlurBottom(true);
            }
        }
    },[])

    const maxHeight = useMemo(() => {
        if (imageRef.current?.clientHeight && interfaceStore.breakpoint !== 'break-point-xs') {
            return imageRef.current?.clientHeight;
        }
        return 0;
    },[blurBottom])

    const imageSrc = getItemImage(props.contentItem, false);

    useMountedEffect(() => {
        props.packeryLayout();
    }, [
        props.contentItem.description,
        props.contentItem.title,
        imageSrc,
        maxHeight,
    ]);

    const descriptionStyle = useMemo(() => {
        if (imageSrc) {
            return {marginLeft: '15px'};
        }
        return {};
    },[])

    const contentContainerClassName = classNames({
        [styles.contentContainer]: true,
        [styles.contentContainerBlurBottom]: blurBottom,
        [styles.contentContainerHidden]: !props.contentItem.published,
    })
    const linkClassName = classNames({
        [styles.link]: true,
        [styles.linkWithDate]: isEvent(props.contentItem),
    })

    const contentImage = useMemo(() => {
        if (isCoursework(props.contentItem)) {
            return getCourseWorkIcons(props.contentItem);
        }
        else {
            return <>
                {imageSrc && !isEvent(props.contentItem) && <div ref={imageRef} className={styles.imageContainer}>
                    <ImageComponent alt={getAltText(props.contentItem)} src={imageSrc} className={styles.image} />
                    {isMedia(props.contentItem) && props.contentItem.json_data.videos?.cover_image_url &&
                        <FontAwesome prefix={'fas'} name={'fa-play-circle'} />}
                </div>}
            </>
        }
    },[])

    return (
        <div className={styles.blockContainer}>
            <div className={styles.container} tabIndex={0} onClick={() => handleContentItemClick(props.contentItem)} onKeyPress={e => {
                if (e.key === "Enter") handleContentItemClick(props.contentItem)}}>
                {isEvent(props.contentItem) && <Media itemObj={props.contentItem} style={styles.dateMedia}/>}
                <div ref={containerRef} className={contentContainerClassName} style={maxHeight > 0 ? {maxHeight: maxHeight} : {}}>
                    <div className={styles.innerContentContainer}>
                        {contentImage}
                        {props.contentItem.organization && <div className={`${styles.organizationTitle} notranslate`}>
                            {props.contentItem.organization.title}
                        </div>}
                        <div className={styles.titleContainer}>
                            {itemTitle && <h2>{itemTitle}</h2>}
                        </div>
                        {isCoursework(props.contentItem) && props.contentItem.json_data?.coursework?.dueDate &&
                            <div className = {styles.dateContainer} style={{fontWeight: 'bold', marginBottom: 0}}>
                                Due Date: {moment(props.contentItem.json_data?.coursework?.dueDate).format('MMM D, YYYY')}
                            </div>}
                        {(isNews(props.contentItem) || isCoursework(props.contentItem)) && <div className={styles.dateContainer}>
                            <div className = {styles.date}>
                                {`Posted: ${date}`}
                            </div>
                        </div>}
                        {isEvent(props.contentItem) && <ItemDetails itemObj={props.contentItem} style={styles.dateLocationContainer}/>}
                        {isFile(props.contentItem) && <ItemDetails itemObj={props.contentItem} style={styles.dateLocationContainer}/>}
                        <div className={styles.descriptionContainer} style={descriptionStyle}>
                            {props.contentItem.description && <SocialMarkdown platform={attribution} text={props.contentItem.description}/>}
                        </div>
                    </div>
                </div>
            </div>
            {blurBottom && <ContentItemReadMore className={linkClassName}
                                                contentItem={props.contentItem}
                                                onClick={handleContentItemClick}/>}
            {currentUserCanModifyAtCurrentOrganization && <div className={styles.editButton}>
                {(props.handleSave && props.handleDelete) && <EditButton
                    handleDelete={props.handleDelete}
                    handleSave={props.handleSave}
                    contentItem={props.contentItem}
                />}
                <button
                    className={styles.hiddenButton}
                    onClick={e => {
                    e.stopPropagation();
                    props.unpublishItem(props.contentItem)
                }} title={currentUserCanModifyAtCurrentOrganization ? "Toggle Item Visibility" : "You do not have permission to toggle item visibility at current SchoolFeed level."} disabled={!currentUserCanModifyAtCurrentOrganization}>
                    <FontAwesome ariaHidden={true} prefix={'far'} name={props.contentItem.published ? 'fa-eye-slash' : 'fa-eye'}/>
                </button>
            </div>}
        </div>
    );
})

export default ContentItemBlock;
